import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { SelectOptionItem } from "../../lib/models/ui";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Badge } from "./badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { ScrollArea } from "./scroll-area";

export function ComboboxMulti<T extends string>({
  dwimOnChange,
  required,
  id,
  placeholder,
  className,
  options,
  value,
  onValueChange,
  disabled,
  isModal,
  tabIndex,
  popoverClassName,
  virtualizeList,
  autofocus,
  maxDisplay = 3,
  showSelectAll = true,
}: {
  dwimOnChange?: boolean;
  required?: boolean;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  options: SelectOptionItem<T>[];
  value: T[];
  onValueChange: (value: T[]) => void;
  className?: string;
  isModal?: boolean;
  tabIndex?: number;
  popoverClassName?: string;
  virtualizeList?: boolean;
  autofocus?: boolean;
  maxDisplay?: number;
  showSelectAll?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const parentRef = React.useRef<HTMLDivElement>(null);

  const filteredOptions = React.useMemo(() => {
    return options.filter((option) => {
      if (!search?.trim()) return true;
      return (
        option.label
          ?.toLocaleLowerCase()
          ?.trim()
          ?.includes(search?.toLocaleLowerCase()?.trim()) || false
      );
    });
  }, [options, search]);

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 35,
    overscan: 10,
  });

  // Force recalculate when dropdown opens
  React.useEffect(() => {
    if (open && virtualizer) {
      const timer = setTimeout(() => {
        virtualizer.measure();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open, virtualizer]);

  const handleSelect = (selectedValue: T) => {
    const newValue = value.includes(selectedValue)
      ? value.filter((v) => v !== selectedValue)
      : [...value, selectedValue];
    onValueChange(newValue);
  };

  const handleSelectAll = () => {
    if (value.length === filteredOptions.length) {
      // Deselect all filtered options
      const filteredValues = filteredOptions.map(option => option.value);
      onValueChange(value.filter(v => !filteredValues.includes(v)));
    } else {
      // Select all filtered options
      const filteredValues = filteredOptions.map(option => option.value);
      const newValue = [...new Set([...value, ...filteredValues])];
      onValueChange(newValue);
    }
  };

  const removeItem = (itemToRemove: T, e: React.MouseEvent) => {
    e.stopPropagation();
    onValueChange(value.filter((v) => v !== itemToRemove));
  };

  const getDisplayText = () => {
    if (value.length === 0) {
      return placeholder || "Select items...";
    }

    if (value.length <= maxDisplay) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((val) => {
            const option = options.find((opt) => opt.value === val);
            return (
              <Badge
                key={val}
                variant="secondary"
                className="text-xs px-2 py-1 flex items-center gap-1"
              >
                {option?.label || val}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={(e) => removeItem(val, e)}
                />
              </Badge>
            );
          })}
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="text-xs px-2 py-1">
          {value.length} selected
        </Badge>
        <div className="flex gap-1">
          {value.slice(0, 2).map((val) => {
            const option = options.find((opt) => opt.value === val);
            return (
              <Badge
                key={val}
                variant="secondary"
                className="text-xs px-2 py-1 flex items-center gap-1"
              >
                {option?.label || val}
                <X
                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                  onClick={(e) => removeItem(val, e)}
                />
              </Badge>
            );
          })}
          {value.length > 2 && (
            <span className="text-sm text-muted-foreground">
              +{value.length - 2} more
            </span>
          )}
        </div>
      </div>
    );
  };

  const isAllFilteredSelected = filteredOptions.length > 0 && 
    filteredOptions.every(option => value.includes(option.value));

  return (
    <Popover open={open} onOpenChange={setOpen} modal={isModal}>
      <PopoverTrigger asChild>
        <Button
          autoFocus={autofocus}
          disabled={disabled}
          tabIndex={tabIndex}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-[200px] justify-between min-h-[40px] h-auto", className)}
        >
          <div className="flex-1 text-left overflow-hidden">
            {getDisplayText()}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-[200px] z-[9999] max-h-52 p-0 overflow-y-scroll", popoverClassName)}
      >
        <Command shouldFilter={false}>
          <CommandInput
            required={required}
            placeholder="Search..."
            value={search}
            onValueChange={setSearch}
          />
          {filteredOptions.length === 0 ? (
            <CommandEmpty>No results found.</CommandEmpty>
          ) : (
            <>
              {showSelectAll && (
                <CommandGroup>
                  <CommandItem
                    onSelect={handleSelectAll}
                    className="font-medium"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isAllFilteredSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {isAllFilteredSelected ? "Deselect All" : "Select All"}
                  </CommandItem>
                </CommandGroup>
              )}
              
              {virtualizeList ? (
                <div
                  ref={parentRef}
                  className="overflow-auto overflow-y-scroll"
                  style={{ height: "200px" }}
                >
                  <div
                    style={{
                      height: `${virtualizer.getTotalSize()}px`,
                      width: "100%",
                      position: "relative",
                    }}
                  >
                    {virtualizer.getVirtualItems().map((virtualRow) => {
                      const option = filteredOptions[virtualRow.index];
                      const isSelected = value.includes(option.value);
                      
                      return (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: `${virtualRow.size}px`,
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                          onSelect={() => handleSelect(option.value)}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              isSelected ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {option.label}
                        </CommandItem>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <CommandGroup>
                  {filteredOptions.map((option) => {
                    const isSelected = value.includes(option.value);
                    
                    return (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={() => handleSelect(option.value)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            isSelected ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {option.label}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
